// @flow

import * as React from "react"
import {connect} from "react-redux"
import {withTranslation} from "react-i18next"
import ClassNames from "classnames"
import {debounce} from "lodash"
import {format} from "date-fns"
import Icon from "./icon"
import {
  toggleSearchListItem,
  markAllAsPhotographed,
  updateSearchItemFound,
} from "../actions/search-items"
import {getBabyPartnerNamesConcat, getBabySexesConcat} from "../reducers/babies"
import {
  isSearchListItemComplete,
  isPhotographedReady,
  hasFoundRemarks,
} from "../reducers/search-items"
import {toDinero} from "../util"
import SearchItemStateToggle from "./search-item-state-toggle"
import type {SearchListItem, SearchItem, Dispatch} from "../types"

// isStandalone means: on the baby admin screen, not on the main dashboard
type Props = {
  +searchListItem: SearchListItem,
  +dispatch: Dispatch,
  +isToggled: boolean,
  +foundRemarksToggleds: $ReadOnlyArray<number>,
  +isStandalone: boolean,
  +t: any,
}

type State = {
  babyRemarksVisibles: $ReadOnlyArray<number>,
  bulkEditItems: $ReadOnlyArray<SearchListItem>,
  clicked: boolean,
  showBulkEditModal: boolean,
  forceOpen: boolean,
  nonForceOpens: number,
}

class SearchItemListItem extends React.Component<Props, State> {
  handleToggle: () => void
  handleToggleBabyRemarks: (number) => void
  handleTogglePhotographed: (SearchItem) => void

  static defaultProps = {
    isToggled: false,
    isStandalone: false,
  }

  state = {
    babyRemarksVisibles: [],
    bulkEditItems: [],
    clicked: false,
    forceOpen: false,
    showBulkEditModal: false,
    nonForceOpens: 0,
  }

  constructor(props: Props) {
    super(props)

    this.handleToggle = this.handleToggle.bind(this)
    this.handleTogglePhotographed = this.handleTogglePhotographed.bind(this)
    this.handleToggleBabyRemarks = this.handleToggleBabyRemarks.bind(this)
  }

  static getDerivedStateFromProps(props, state) {
    let nonForceOpens = state.nonForceOpens - 1
    if (nonForceOpens < 0) {
      nonForceOpens = 0
    }
    return {
      forceOpen: state.clicked && state.nonForceOpens == 0,
      nonForceOpens,
    }
  }

  handleToggle() {
    const {
      dispatch,
      searchListItem: {productId},
    } = this.props
    dispatch(toggleSearchListItem(productId))
  }

  handleTogglePhotographed(item: SearchItem) {
    const {dispatch} = this.props
    this.setState({forceOpen: false, nonForceOpens: 1}, () => {
      dispatch(
        updateSearchItemFound(
          item.productId,
          item.id,
          item.babyListItemFoundId,
          item.uniqId,
          item.state,
          !item.isPhotographed,
          item.babyListItemFoundRemarks
        )
      )
    })
  }

  handleToggleBabyRemarks(itemId: number) {
    const current = this.state.babyRemarksVisibles
    const next = current.includes(itemId)
      ? current.filter((i) => i != itemId)
      : [...current, itemId]
    this.setState({babyRemarksVisibles: next})
  }

  renderDetails(items: $ReadOnlyArray<SearchItem>) {
    return items.length > 1
      ? this.renderDetailsMulti(items)
      : this.renderDetailsSingle(items[0])
  }

  renderDetailsSingle(item: SearchItem) {
    const {dispatch, t, isStandalone, foundRemarksToggleds} = this.props
    return (
      <DetailSingleItem
        item={item}
        dispatch={dispatch}
        isStandalone={isStandalone}
        t={t}
        isFoundRemarksToggled={foundRemarksToggleds.includes(item.uniqId)}
        babyRemarksVisibles={this.state.babyRemarksVisibles}
        onTogglePhotographed={() => {}}
        onToggleBabyRemarks={() => this.handleToggleBabyRemarks(item.uniqId)}
      />
    )
  }

  /**
   * Multi items are like '3 x Hoeslakentje'
   * When going standalone (on baby detail) there is no use repeating some things, since they will always be equal.
   */
  renderDetailsMultiStandaloneHeader(items: $ReadOnlyArray<SearchItem>) {
    const {t} = this.props
    const item = items[0]
    if (item.remarks && item.remarks !== "") {
      return (
        <div className="justify-content-between flex mt-2">
          <div className="remarks">
            <strong>
              {t("dashboard.searchItems.searchItem.remarksIntakePrefix")}
            </strong>{" "}
            {item.remarks}
          </div>

          <div className="search-item-meta">
            <span className="flags">
              {item.isStar && <Icon name="icon-star" />}
              {item.isPimp && <Icon name="icon-brush" />}
              {item.isExtra && <Icon name="icon-calculator" />}
              {item.isCritical && <Icon name="icon-warning-alt" />}
            </span>
            {item.state == "ok" && this.renderPhotographed(item)}
          </div>
        </div>
      )
    }
    return null
  }

  renderDetailsMulti(items: $ReadOnlyArray<SearchItem>) {
    const {dispatch, isStandalone, t, foundRemarksToggleds} = this.props
    return (
      <>
        {isStandalone && this.renderDetailsMultiStandaloneHeader(items)}
        <ul className="search-item-items">
          {items.map((item, idx) => (
            <DetailsMultiItem
              key={`subitem-${item.id}-${idx}`}
              item={item}
              dispatch={dispatch}
              isStandalone={isStandalone}
              t={t}
              isFoundRemarksToggled={foundRemarksToggleds.includes(item.uniqId)}
              babyRemarksVisibles={this.state.babyRemarksVisibles}
              onTogglePhotographed={this.handleTogglePhotographed}
              onToggleBabyRemarks={() =>
                this.handleToggleBabyRemarks(item.uniqId)
              }
            />
          ))}
        </ul>
      </>
    )
  }

  renderStateWarning(item: SearchItem) {
    const {t} = this.props
    if (!["signed", "born", "notif_mmm_webshop"].includes(item.babyState)) {
      return (
        <span className="badge badge-danger">
          {t(`babyCard.babyState.${item.babyState}`)}
        </span>
      )
    }
    return null
  }

  renderPhotographed(item: SearchListItem) {
    // const {onTogglePhotographed} = this.props
    if (item.items == null) return
    const {dispatch} = this.props
    const allPhotographed =
      item.items.filter((i) => i.isPhotographed).length == item.items.length
    const classes = ClassNames("search-item-photographed-status", "ml-2", {
      "is-photographed": allPhotographed,
    })
    const _item = item.items[0]
    return (
      <Icon
        name="icon-camera"
        className={classes}
        onClick={() => {
          if (item.items.length == 1) {
            this.handleTogglePhotographed(_item)
          } else {
            dispatch(markAllAsPhotographed([item]))
          }
        }}
      />
    )
  }

  render() {
    const {searchListItem, isToggled, isStandalone, foundRemarksToggleds} =
      this.props
    const {amount, productId, items} = searchListItem
    const item = items[0]
    let divAmount =
      amount > 1
        ? items.filter((i) => i.babyListId == item.babyListId).length
        : amount
    if (!item) return null
    const priceSelling = toDinero(item.priceSelling).divide(divAmount)
    const classNames = ClassNames("search-item", {
      "has-remarks": searchListItem.hasRemarks,
    })
    return (
      <li className="search-item-list-item action-menu-remote-trigger">
        <div className={classNames}>
          <SearchItemStateToggle
            babyListItemFoundId={item.babyListItemFoundId}
            babyListItemId={item.id}
            isMultiComplete={isSearchListItemComplete(searchListItem)}
            isSingle={items.length == 1}
            isStandalone={isStandalone}
            onClick={() => {
              this.setState({clicked: true})
            }}
            productId={productId}
            searchListItem={searchListItem}
            searchListItemId={item.uniqId}
            state={item.state}
          />
          <div className="search-item-main">
            <div onClick={this.handleToggle}>
              {amount > 1 && (
                <span className="search-item-amount">{amount} x</span>
              )}
              <span>{item.name} </span>
              <span className="text-gray">{item.subname} </span>
              {items.length == 1 && this.renderStateWarning(item)}{" "}
              {searchListItem.hasRemarks && (
                <Icon className="comment-bubble" name="icon-commenting-o" />
              )}{" "}
              {item.isCircular && (
                <Icon
                  className="comment-bubble pl-1 pr-1"
                  name="icon-recycle"
                />
              )}{" "}
              {(item.isStar || (item.parent && item.parent.isStar)) && (
                <Icon className="comment-bubble pl-1 pr-1" name="icon-star" />
              )}{" "}
              {hasFoundRemarks(searchListItem) && (
                <Icon
                  className="comment-bubble mr-2 pt-2"
                  style={{fill: "orange"}}
                  name="icon-warning-alt"
                />
              )}{" "}
              <span className="small text-gray pl-1 is-nfw">
                <span>{toDinero(item.pricePurchase).toFormat("$0,0.00")}</span>
                <span> / </span>
                <span>{priceSelling.toFormat("$0,0.00")}</span>
              </span>
            </div>
            {isPhotographedReady(searchListItem) &&
              this.renderPhotographed(searchListItem)}
          </div>
          {((isToggled && items.length > 0) ||
            itemHasFoundRemarksToggled(searchListItem, foundRemarksToggleds)) &&
            this.renderDetails(items)}
        </div>
      </li>
    )
  }
}

// TODO MOVE INTO REDUCER
const itemHasFoundRemarksToggled = (
  searchListItem: SearchListItem,
  foundRemarksToggleds: $ReadOnlyArray<number>
) => {
  return (
    searchListItem.items
      .map((i) => foundRemarksToggleds.includes(i.uniqId))
      .filter((v) => v == true).length > 0
  )
}

export default withTranslation()(connect()(SearchItemListItem))

type DetailSingleItemProps = {
  +t: any,
  item: SearchItem,
  dispatch: Dispatch,
  isStandalone: boolean,
  babyRemarksVisibles: $ReadOnlyArray<number>,
  onToggleBabyRemarks: () => void,
  onTogglePhotographed: (SearchItem) => void,
  isFoundRemarksToggled: boolean,
}

type DetailSingleItemState = {
  foundRemarks: string,
}

class DetailSingleItem extends React.Component<
  DetailSingleItemProps,
  DetailSingleItemState,
> {
  handleFoundRemarksChange: (SyntheticInputEvent<HTMLInputElement>) => void
  saveRemarks: () => void

  constructor(props: DetailSingleItemProps) {
    super(props)
    this.state = {
      foundRemarks: this.props.item.babyListItemFoundRemarks,
    }
    this.handleFoundRemarksChange = this.handleFoundRemarksChange.bind(this)
    this.saveRemarks = debounce(this.saveRemarks.bind(this), 400, {
      leading: false,
      trailing: true,
    })
  }

  handleFoundRemarksChange(e: SyntheticInputEvent<HTMLInputElement>) {
    this.setState({foundRemarks: e.target.value}, () => this.saveRemarks())
  }

  saveRemarks() {
    const {item, dispatch} = this.props
    const {foundRemarks} = this.state
    dispatch(
      updateSearchItemFound(
        item.productId,
        item.id,
        item.babyListItemFoundId,
        item.searchListItemId,
        item.state,
        item.isPhotographed,
        foundRemarks
      )
    )
  }

  render() {
    const {t, item, isStandalone} = this.props
    const {foundRemarks} = this.state
    return (
      <div className="search-item-details">
        <div className="r">
          <div className="search-item-name">
            <a href={`/admin/babies/${item.baby.id}`} className="set-link">
              {getBabyPartnerNamesConcat(item.baby)}
            </a>
            <span className="ml-1 text-gray">
              {getBabySexesConcat(item.baby)}
            </span>
          </div>
          <div className="search-item-meta">
            {!isStandalone &&
              item.baby.moodboardType &&
              item.baby.moodboardType !== "" && (
                <span className="moodboard-type">
                  {item.baby.moodboardType}
                </span>
              )}
            <span className="flags">
              {item.isStar && <Icon name="icon-star" />}
              {item.isPimp && <Icon name="icon-brush" />}
              {item.isExtra && <Icon name="icon-calculator" />}
              {item.isCritical && <Icon name="icon-warning-alt" />}
            </span>
            {!isStandalone && (
              <time className="small">
                {format(item.baby.pickupDate, "DD/MM/YY")}
              </time>
            )}
          </div>
        </div>
        {item.remarks && item.remarks !== "" && (
          <div className="r">
            <div className="remarks">
              <strong>
                {t("dashboard.searchItems.searchItem.remarksIntakePrefix")}
              </strong>{" "}
              {item.remarks}
            </div>
          </div>
        )}
        <div className="r">
          {item.state == "todo" && (
            <div className="form-group w-100 mt-2">
              <label htmlFor="remarks">
                {t("dashboard.searchItems.searchItem.foundRemarks")}
              </label>
              <textarea
                className="form-control w-full"
                value={foundRemarks || ""}
                onChange={this.handleFoundRemarksChange}
                name="remarks"
              />
            </div>
          )}
        </div>
      </div>
    )
  }
}

type DetailsMultiItemProps = {
  +t: any,
  item: SearchItem,
  dispatch: Dispatch,
  isStandalone: boolean,
  babyRemarksVisibles: $ReadOnlyArray<number>,
  onToggleBabyRemarks: () => void,
  onTogglePhotographed: (SearchItem) => void,
  isFoundRemarksToggled: boolean,
}

type DetailsMultiItemState = {
  foundRemarks: string,
  isOpen: boolean,
}

class DetailsMultiItem extends React.Component<
  DetailsMultiItemProps,
  DetailsMultiItemState,
> {
  handleFoundRemarksChange: (SyntheticInputEvent<HTMLInputElement>) => void
  saveRemarks: () => void

  constructor(props: DetailSingleItemProps) {
    super(props)
    this.state = {
      foundRemarks: this.props.item.babyListItemFoundRemarks,
      isOpen: false,
    }
    this.handleFoundRemarksChange = this.handleFoundRemarksChange.bind(this)
    this.saveRemarks = debounce(this.saveRemarks.bind(this), 400, {
      leading: false,
      trailing: true,
    })
  }

  handleFoundRemarksChange(e: SyntheticInputEvent<HTMLInputElement>) {
    this.setState({foundRemarks: e.target.value}, () => this.saveRemarks())
  }

  saveRemarks() {
    const {item, dispatch} = this.props
    const {foundRemarks} = this.state
    dispatch(
      updateSearchItemFound(
        item.productId,
        item.id,
        item.babyListItemFoundId,
        item.searchListItemId,
        item.state,
        item.isPhotographed,
        foundRemarks
      )
    )
  }

  renderStateWarning(item: SearchItem) {
    const {t} = this.props
    if (item.babyState !== "signed" && item.babyState !== "born") {
      return (
        <span className="badge badge-danger">
          {t(`babyCard.babyState.${item.babyState}`)}
        </span>
      )
    }
    return null
  }

  renderSearchItemName() {
    const {item, isStandalone} = this.props
    const {foundRemarks} = this.state
    if (isStandalone) {
      return <div className="search-item-name">{item.name}</div>
    } else {
      return (
        <div className="search-item-name">
          <a href={`/admin/babies/${item.baby.id}`} className="set-link">
            {getBabyPartnerNamesConcat(item.baby)}
          </a>
          <span className="ml-1 text-gray">
            {getBabySexesConcat(item.baby)}
          </span>
          {item.remarks !== "" && (
            <Icon
              className="comment-bubble ml-2"
              name="icon-commenting-o"
              onClick={() => {}}
            />
          )}{" "}
          {this.renderStateWarning(item)}
          {item.state == "todo" && foundRemarks != "" && (
            <Icon
              name="icon-warning-alt"
              className="comment-bubble mr-2 pt-2"
              style={{fill: "orange"}}
            />
          )}
          {item.isCritical && (
            <Icon name="icon-warning-alt" className="comment-bubble ml-2" />
          )}
        </div>
      )
    }
  }

  render() {
    const {t, item, isStandalone} = this.props
    const {foundRemarks} = this.state
    return (
      <li className="search-item-subitem">
        <SearchItemStateToggle
          state={item.state}
          babyListItemId={item.id}
          babyListItemFoundId={item.babyListItemFoundId}
          isStandalone={isStandalone}
          productId={item.productId}
          searchListItemId={item.uniqId}
        />
        <div className="search-item-subitem-main">
          {this.renderSearchItemName()}
          <SearchItemMeta
            isStandalone={isStandalone}
            item={item}
            onTogglePhotographed={this.props.onTogglePhotographed}
          />
        </div>
        {item.remarks && item.remarks !== "" && !isStandalone && (
          <div className="remarks mt-2 mb-2">
            <strong>
              {t("dashboard.searchItems.searchItem.remarksIntakePrefix")}
            </strong>{" "}
            {item.remarks}
          </div>
        )}
        {item.state == "todo" && foundRemarks != "" && (
          <div className="form-group w-100 mt-2">
            <label htmlFor="remarks">
              {t("dashboard.searchItems.searchItem.foundRemarks")}
            </label>
            <textarea
              className="form-control w-full"
              value={foundRemarks || ""}
              onChange={this.handleFoundRemarksChange}
              name="remarks"
            />
          </div>
        )}
      </li>
    )
  }
}

class SearchItemMeta extends React.Component<*> {
  renderPhotographed(item: SearchItem) {
    const {onTogglePhotographed} = this.props
    const classes = ClassNames("search-item-photographed-status", "ml-2", {
      "is-photographed": item.isPhotographed,
    })
    return (
      <Icon
        name="icon-camera"
        className={classes}
        onClick={() => onTogglePhotographed(item)}
      />
    )
  }

  render() {
    const {item, isStandalone} = this.props
    if (isStandalone) {
      return (
        <div className="search-item-meta">
          {item.state && this.renderPhotographed(item)}
        </div>
      )
    } else {
      return (
        <div className="search-item-meta">
          {item.baby.moodboardType && item.baby.moodboardType !== "" && (
            <span className="moodboard-type">{item.baby.moodboardType}</span>
          )}
          <span className="flags">
            {item.isStar && <Icon name="icon-star" />}
            {item.isPimp && <Icon name="icon-brush" />}
            {item.isExtra && <Icon name="icon-calculator" />}
            {item.isCritical && <Icon name="icon-warning-alt" />}
          </span>

          <time className="small">
            {format(item.baby.pickupDate, "DD/MM/YY")}
          </time>
          {item.state != null && this.renderPhotographed(item)}
        </div>
      )
    }
  }
}
